import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuTrigger,
 DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
 User,
 LogOut,
 Settings,
 CreditCard,
 Terminal,
 LayoutDashboard,
 Files,
 Radio,
 ChevronDown,
 Activity,
 AlertTriangle,
} from 'lucide-react';
import {
 AlertDialog,
 AlertDialogAction,
 AlertDialogCancel,
 AlertDialogContent,
 AlertDialogDescription,
 AlertDialogFooter,
 AlertDialogHeader,
 AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';

export function Navigation() {
 const { user } = useAuth();
 const location = useLocation();

 // Check if "active" session: on interview route AND has profile selected
 const isInterviewActive =
  location.pathname.startsWith('/interview/') &&
  location.search.includes('jobProfileId=');

 const [showExitConfirm, setShowExitConfirm] = useState(false);
 const [pendingPath, setPendingPath] = useState<string | null>(null);

 const isActive = (path: string) => location.pathname === path;

 const handleNavClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  path: string
 ) => {
  if (isInterviewActive && path !== location.pathname) {
   e.preventDefault();
   setPendingPath(path);
   setShowExitConfirm(true);
  }
 };

 const confirmExit = () => {
  if (pendingPath) {
   setShowExitConfirm(false);
   // Force full reload or navigate
   window.location.href = pendingPath;
  }
 };

 const NavLinks = () => (
  <>
   <Link
    to='/dashboard'
    onClick={(e) => handleNavClick(e, '/dashboard')}
    className={`flex items-center space-x-2 transition-colors hover:text-primary ${
     isActive('/dashboard')
      ? 'text-primary border-b-2 border-primary h-16'
      : 'text-muted-foreground'
    }`}
   >
    <LayoutDashboard className='h-4 w-4' />
    <span className='font-mono tracking-wide'>DASHBOARD</span>
   </Link>
   <Link
    to='/profiles'
    onClick={(e) => handleNavClick(e, '/profiles')}
    className={`flex items-center space-x-2 transition-colors hover:text-primary ${
     isActive('/profiles')
      ? 'text-primary border-b-2 border-primary h-16'
      : 'text-muted-foreground'
    }`}
   >
    <Files className='h-4 w-4' />
    <span className='font-mono tracking-wide'>INTERVIEWS</span>
   </Link>
   <Link
    to='/interview'
    onClick={(e) => {
     if (isInterviewActive) {
      // If active, disable clicking "Session" (it's current context)
      // Or if they really want to click it, we could do nothing
      e.preventDefault();
     } else {
      handleNavClick(e, '/interview');
     }
    }}
    className={`flex items-center space-x-2 transition-colors hover:text-primary ${
     isInterviewActive ? 'opacity-50 cursor-not-allowed' : ''
    } ${
     isActive('/interview')
      ? 'text-primary border-b-2 border-primary h-16'
      : 'text-muted-foreground'
    }`}
   >
    <Radio className='h-4 w-4' />
    <span className='font-mono tracking-wide'>SESSION</span>
   </Link>
  </>
 );

 return (
  <header className='sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
   <div className='flex h-16 items-center px-4 md:px-6 justify-between'>
    {/* Logo */}
    <div className='flex items-center space-x-2 mr-4'>
     <Terminal className='h-6 w-6 text-primary' />
     <span className='font-mono text-lg font-bold tracking-tighter'>
      MOCK.AI
     </span>
     <div className='hidden md:flex items-center gap-2 px-2 py-1 bg-primary/10 border border-primary/20 rounded-none ml-4'>
      <div className='w-1.5 h-1.5 rounded-full bg-primary animate-pulse' />
      <span className='text-[10px] font-mono text-primary font-bold tracking-wider'>
       ONLINE
      </span>
     </div>
    </div>

    {/* Desktop Navigation */}
    <nav className='hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center space-x-6 text-sm font-medium'>
     <NavLinks />
    </nav>

    {/* Right Side Actions */}
    <div className='flex items-center space-x-2 md:space-x-4'>
     <ThemeToggle />

     {/* Mobile/Tablet Menu */}
     <div className='lg:hidden'>
      <DropdownMenu>
       <DropdownMenuTrigger asChild>
        <Button variant='ghost' size='icon' className='rounded-none'>
         <div className='space-y-1.5'>
          <div className='w-6 h-0.5 bg-foreground'></div>
          <div className='w-6 h-0.5 bg-foreground'></div>
          <div className='w-6 h-0.5 bg-foreground'></div>
         </div>
        </Button>
       </DropdownMenuTrigger>
       <DropdownMenuContent
        align='end'
        className='w-[80vw] sm:w-[300px] rounded-none border-border bg-background mt-2'
       >
        <div className='p-4 space-y-4'>
         <div className='space-y-2'>
          <div className='font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2'>
           Navigation
          </div>
          <Link
           to='/dashboard'
           onClick={(e) => handleNavClick(e, '/dashboard')}
           className='flex items-center p-2 hover:bg-muted/50 text-sm font-mono tracking-wide'
          >
           <LayoutDashboard className='mr-3 h-4 w-4 text-primary' />
           DASHBOARD
          </Link>
          <Link
           to='/profiles'
           onClick={(e) => handleNavClick(e, '/profiles')}
           className='flex items-center p-2 hover:bg-muted/50 text-sm font-mono tracking-wide'
          >
           <Files className='mr-3 h-4 w-4 text-primary' />
           INTERVIEWS
          </Link>
          <Link
           to='/interview'
           onClick={(e) => {
            if (isInterviewActive) {
             e.preventDefault();
            } else {
             handleNavClick(e, '/interview');
            }
           }}
           className={`flex items-center p-2 hover:bg-muted/50 text-sm font-mono tracking-wide ${isInterviewActive ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
           <Radio className='mr-3 h-4 w-4 text-primary' />
           SESSION
          </Link>
         </div>

         {user && (
          <>
           <DropdownMenuSeparator />
           <div className='space-y-2'>
            <div className='font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2'>
             User: {user.fullName}
            </div>
            <Link
             to='/profile'
             className='flex items-center p-2 hover:bg-muted/50 text-sm font-mono tracking-wide'
            >
             <User className='mr-3 h-4 w-4 text-muted-foreground' />
             PROFILE
            </Link>
            <Link
             to='/settings'
             className='flex items-center p-2 hover:bg-muted/50 text-sm font-mono tracking-wide'
            >
             <Settings className='mr-3 h-4 w-4 text-muted-foreground' />
             SETTINGS
            </Link>
            <Link
             to='/logout'
             className='flex items-center p-2 hover:bg-red-500/10 text-red-500 cursor-pointer text-sm font-mono tracking-wide transition-colors'
            >
             <LogOut className='mr-3 h-4 w-4' />
             LOG OUT
            </Link>
           </div>
          </>
         )}

         {!user && (
          <div className='grid grid-cols-2 gap-2 pt-2'>
           <Button
            asChild
            variant='ghost'
            className='rounded-none font-mono text-xs w-full justify-center'
           >
            <Link to='/auth/login'>LOGIN</Link>
           </Button>
           <Button
            asChild
            className='rounded-none font-mono text-xs w-full justify-center'
           >
            <Link to='/auth/register'>JOIN</Link>
           </Button>
          </div>
         )}
        </div>
       </DropdownMenuContent>
      </DropdownMenu>
     </div>

     {/* Desktop User Menu */}
     <div className='hidden lg:block'>
      {user ? (
       <DropdownMenu>
        <DropdownMenuTrigger asChild>
         <Button
          variant='ghost'
          className='relative h-10 w-auto rounded-none border border-transparent hover:border-border hover:bg-muted/50 px-3 pl-4 transition-all duration-200 group'
         >
          <div className='flex flex-col items-end mr-3'>
           <span className='text-xs font-bold leading-none tracking-wide'>
            {user.fullName?.toUpperCase()}
           </span>
           <span className='text-[10px] text-muted-foreground font-mono mt-1 group-hover:text-primary transition-colors'>
            ID: {user.id.slice(0, 8)}
           </span>
          </div>
          <ChevronDown className='h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors' />
         </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
         className='w-56 rounded-none border-border bg-background'
         align='end'
         forceMount
        >
         <div className='flex items-center justify-start gap-2 p-2 text-xs font-mono text-muted-foreground border-b border-border mb-1'>
          <Activity className='w-3 h-3' />
          <span>ACCOUNT_ACTIVE</span>
         </div>
         <DropdownMenuItem
          asChild
          className='rounded-none cursor-pointer focus:bg-primary/10 focus:text-primary font-mono text-xs py-2.5'
         >
          <Link to='/profile' className='flex w-full items-center'>
           <User className='mr-2 h-4 w-4' />
           <span>PROFILE</span>
          </Link>
         </DropdownMenuItem>
         <DropdownMenuItem
          asChild
          className='rounded-none cursor-pointer focus:bg-primary/10 focus:text-primary font-mono text-xs py-2.5'
         >
          <Link to='/settings' className='flex w-full items-center'>
           <Settings className='mr-2 h-4 w-4' />
           <span>SETTINGS</span>
          </Link>
         </DropdownMenuItem>
         <DropdownMenuItem
          asChild
          className='rounded-none cursor-pointer focus:bg-primary/10 focus:text-primary font-mono text-xs py-2.5'
         >
          <Link to='/billing' className='flex w-full items-center'>
           <CreditCard className='mr-2 h-4 w-4' />
           <span>BILLING</span>
          </Link>
         </DropdownMenuItem>
         <DropdownMenuSeparator className='bg-border' />
         <DropdownMenuItem
          className='rounded-none cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10 font-mono text-xs py-2.5'
          asChild
         >
          <Link to='/logout' className='flex w-full items-center'>
           <LogOut className='mr-2 h-4 w-4' />
           <span>LOG OUT</span>
          </Link>
         </DropdownMenuItem>
        </DropdownMenuContent>
       </DropdownMenu>
      ) : (
       <div className='flex gap-2'>
        <Button
         asChild
         variant='ghost'
         className='rounded-none font-mono text-xs'
        >
         <Link to='/auth/login'>LOGIN</Link>
        </Button>
        <Button asChild className='rounded-none font-mono text-xs'>
         <Link to='/auth/register'>GET_STARTED</Link>
        </Button>
       </div>
      )}
     </div>
    </div>
   </div>

   <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
    <AlertDialogContent className='bg-background border-border sm:max-w-[425px] rounded-none'>
     <AlertDialogHeader>
      <div className='flex items-center gap-2 text-yellow-500 mb-2'>
       <AlertTriangle className='h-5 w-5' />
       <AlertDialogTitle className='font-mono'>
        ACTIVE SESSION WARNING
       </AlertDialogTitle>
      </div>
      <AlertDialogDescription className='font-mono text-xs'>
       Standard protocol warning: You are about to terminate an active
       simulation. All session data will be saved, but the context link will be
       severed.
       <br />
       <br />
       Are you sure you want to abort?
      </AlertDialogDescription>
     </AlertDialogHeader>
     <AlertDialogFooter>
      <AlertDialogCancel className='rounded-none font-mono text-xs'>
       CANCEL
      </AlertDialogCancel>
      <AlertDialogAction
       onClick={confirmExit}
       className='rounded-none bg-red-600 hover:bg-red-700 font-mono text-xs'
      >
       TERMINATE SESSION
      </AlertDialogAction>
     </AlertDialogFooter>
    </AlertDialogContent>
   </AlertDialog>
  </header>
 );
}
